import React from 'react';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, Clock, Eye } from 'lucide-react';

export type KycStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'KEEP_IN_VIEW' | string;

interface KycStatusBadgeProps {
    status: KycStatus;
    size?: 'sm' | 'md' | 'lg';
    className?: string;
}

const KycStatusBadge: React.FC<KycStatusBadgeProps> = ({ status, size = 'md', className = '' }) => {
    const statusUpper = status?.toUpperCase() || 'PENDING';
    const normalizedStatus = statusUpper.replace(/[\s-]+/g, '_');
    const canonicalStatus =
        normalizedStatus === 'KIV' || normalizedStatus === 'KEEPINVIEW'
            ? 'KEEP_IN_VIEW'
            : normalizedStatus;

    const getStatusConfig = () => {
        switch (canonicalStatus) {
            case 'APPROVED':
                return {
                    label: 'Approved',
                    className: 'bg-success text-success-foreground',
                    icon: CheckCircle,
                };
            case 'REJECTED':
                return {
                    label: 'Rejected',
                    className: 'bg-destructive text-destructive-foreground',
                    icon: XCircle,
                };
            case 'KEEP_IN_VIEW':
            case 'KEEPINVIEW':
                return {
                    label: 'Keep in View',
                    className: 'bg-blue-500 text-white',
                    icon: Eye,
                };
            case 'PENDING':
            default:
                return {
                    label: 'Pending',
                    className: 'bg-warning text-warning-foreground',
                    icon: Clock,
                };
        }
    };

    const config = getStatusConfig();
    const Icon = config.icon;

    const sizeClasses = {
        sm: 'text-xs px-2 py-0.5',
        md: 'text-sm px-2.5 py-1',
        lg: 'text-base px-3 py-1.5',
    };

    const iconSizes = {
        sm: 'w-3 h-3',
        md: 'w-4 h-4',
        lg: 'w-5 h-5',
    };

    return (
        <Badge className={`${config.className} ${sizeClasses[size]} ${className} inline-flex items-center gap-1.5`}>
            <Icon className={iconSizes[size]} />
            {config.label}
        </Badge>
    );
};

export default KycStatusBadge;
